import { useState, useEffect } from 'react';
import { getPrinters, createPrinter, updatePrinter, deletePrinter, getEquipment, createEquipment, updateEquipment, deleteEquipment } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Printer, Zap, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const defaultPrinter = {
  printer_name: '',
  printer_cost: 300,
  estimated_life_hours: 5000,
  electricity_cost_kwh: 0.25,
  average_power_watts: 200,
  maintenance_cost_per_hour: 0
};

export default function SettingsPage() {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [formData, setFormData] = useState(defaultPrinter);

  // Attrezzature (Equipment 3D — AMS, piatti, ecc.)
  const defaultEq = { name: '', brand: '', price: 0, life_hours: 5000, notes: '' };
  const [equipment, setEquipment] = useState([]);
  const [eqDialogOpen, setEqDialogOpen] = useState(false);
  const [editingEq, setEditingEq] = useState(null);
  const [eqForm, setEqForm] = useState(defaultEq);

  useEffect(() => {
    loadPrinters();
    loadEquipment();
  }, []);

  const loadEquipment = async () => {
    try {
      const data = await getEquipment();
      setEquipment(data);
    } catch {
      // silent
    }
  };

  const handleEqSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEq) {
        await updateEquipment(editingEq.id, eqForm);
        toast.success('Attrezzatura aggiornata');
      } else {
        await createEquipment(eqForm);
        toast.success('Attrezzatura aggiunta');
      }
      setEqDialogOpen(false);
      setEditingEq(null);
      setEqForm(defaultEq);
      loadEquipment();
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEqEdit = (eq) => {
    setEditingEq(eq);
    setEqForm({ name: eq.name, brand: eq.brand || '', price: eq.price, life_hours: eq.life_hours, notes: eq.notes || '' });
    setEqDialogOpen(true);
  };

  const handleEqDelete = async (id) => {
    if (!window.confirm('Eliminare questa attrezzatura?')) return;
    try {
      await deleteEquipment(id);
      toast.success('Attrezzatura eliminata');
      loadEquipment();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const openNewEqDialog = () => {
    setEditingEq(null);
    setEqForm(defaultEq);
    setEqDialogOpen(true);
  };

  const eqPreviewAmort = eqForm.life_hours > 0 ? eqForm.price / eqForm.life_hours : 0;

  const loadPrinters = async () => {
    try {
      const data = await getPrinters();
      setPrinters(data);
    } catch (err) {
      toast.error('Errore nel caricamento stampanti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPrinter) {
        await updatePrinter(editingPrinter.id, formData);
        toast.success('Stampante aggiornata');
      } else {
        await createPrinter(formData);
        toast.success('Stampante aggiunta');
      }
      setDialogOpen(false);
      setEditingPrinter(null);
      setFormData(defaultPrinter);
      loadPrinters();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (printer) => {
    setEditingPrinter(printer);
    setFormData({
      printer_name: printer.printer_name,
      printer_cost: printer.printer_cost,
      estimated_life_hours: printer.estimated_life_hours,
      electricity_cost_kwh: printer.electricity_cost_kwh,
      average_power_watts: printer.average_power_watts,
      maintenance_cost_per_hour: printer.maintenance_cost_per_hour || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa stampante?')) return;
    try {
      await deletePrinter(id);
      toast.success('Stampante eliminata');
      loadPrinters();
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const openNewDialog = () => {
    setEditingPrinter(null);
    setFormData(defaultPrinter);
    setDialogOpen(true);
  };

  // Calculate preview values
  const previewDepreciation = formData.estimated_life_hours > 0 
    ? formData.printer_cost / formData.estimated_life_hours 
    : 0;
  const previewElectricity = (formData.average_power_watts / 1000) * formData.electricity_cost_kwh;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Impostazioni</h1>
        <p className="text-muted-foreground mt-1">Gestisci stampanti e costi fissi</p>
      </div>

      {/* Printers Section */}
      <Card className="border-border/40">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading">Stampanti 3D</CardTitle>
            <CardDescription>Configura le tue stampanti per calcolare ammortamento ed elettricità</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} data-testid="add-printer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingPrinter ? 'Modifica Stampante' : 'Nuova Stampante'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Stampante</Label>
                  <Input
                    value={formData.printer_name}
                    onChange={(e) => setFormData({...formData, printer_name: e.target.value})}
                    placeholder="Es. Bambu Lab X1C"
                    required
                    data-testid="printer-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Costo Stampante (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.printer_cost}
                      onChange={(e) => setFormData({...formData, printer_cost: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                      data-testid="printer-cost-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vita Stimata (ore)</Label>
                    <Input
                      type="number"
                      value={formData.estimated_life_hours}
                      onChange={(e) => setFormData({...formData, estimated_life_hours: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                      data-testid="printer-life-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Costo kWh (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.electricity_cost_kwh}
                      onChange={(e) => setFormData({...formData, electricity_cost_kwh: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                      data-testid="electricity-cost-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Potenza Media (W)</Label>
                    <Input
                      type="number"
                      value={formData.average_power_watts}
                      onChange={(e) => setFormData({...formData, average_power_watts: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                      data-testid="power-watts-input"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label title="Costo orario di manutenzione e riparazioni (es. ugelli, lubrificanti, cinghie). Verra' usato come default nel calcolatore.">Manutenzione (€/ora di stampa)</Label>
                    <Input
                      type="number" step="0.01"
                      value={formData.maintenance_cost_per_hour}
                      onChange={(e) => setFormData({...formData, maintenance_cost_per_hour: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                      placeholder="0.05"
                      data-testid="maintenance-per-hour-input"
                    />
                    <p className="text-[10px] text-muted-foreground">Esempio: ugello sostitutivo ogni 1000h → 0.01-0.05 €/h. Lasciare 0 se non vuoi conteggiare.</p>
                  </div>
                </div>

                {/* Preview */}
                <Card className="bg-muted/50 border-dashed">
                  <CardContent className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ammortamento/ora:</span>
                      <span className="font-mono text-primary">€{previewDepreciation.toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Elettricità/ora:</span>
                      <span className="font-mono text-primary">€{previewElectricity.toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t border-border pt-2">
                      <span>Costo Fisso/ora:</span>
                      <span className="font-mono">€{(previewDepreciation + previewElectricity).toFixed(4)}</span>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="save-printer-btn">
                    {editingPrinter ? 'Aggiorna' : 'Aggiungi'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
          ) : printers.length === 0 ? (
            <div className="text-center py-8">
              <Printer className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold mb-2">Nessuna stampante</h3>
              <p className="text-muted-foreground mb-4">Aggiungi la tua prima stampante 3D</p>
              <Button onClick={openNewDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi Stampante
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {printers.map(printer => (
                <Card key={printer.id} className="bg-muted/30 group" data-testid={`printer-card-${printer.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                          <Printer className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold">{printer.printer_name}</h3>
                          <p className="text-sm text-muted-foreground">€{printer.printer_cost}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8"
                          onClick={() => handleEdit(printer)}
                          data-testid={`edit-printer-${printer.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(printer.id)}
                          data-testid={`delete-printer-${printer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Vita:</span>
                        <span className="font-mono">{printer.estimated_life_hours}h</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Potenza:</span>
                        <span className="font-mono">{printer.average_power_watts}W</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-border/40 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground block">Ammort./h</span>
                        <span className="font-mono text-primary">€{printer.depreciation_per_hour?.toFixed(4)}</span>
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground block">Elettr./h</span>
                        <span className="font-mono text-primary">€{printer.electricity_cost_per_hour?.toFixed(4)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attrezzature 3D Section */}
      <Card className="border-border/40" data-testid="equipment-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Attrezzature & Accessori</CardTitle>
            <CardDescription>Attrezzature amortizzate a tempo (es. Bambu Lab AMS, piatti texturati, sistemi multicolore)</CardDescription>
          </div>
          <Dialog open={eqDialogOpen} onOpenChange={setEqDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewEqDialog} data-testid="add-equipment-btn">
                <Plus className="w-4 h-4 mr-2" />Aggiungi
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingEq ? 'Modifica Attrezzatura' : 'Nuova Attrezzatura'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEqSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input value={eqForm.name} onChange={e => setEqForm({...eqForm, name: e.target.value})} placeholder="Es. Bambu Lab AMS 2 Pro" required data-testid="equipment-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Marca</Label>
                  <Input value={eqForm.brand} onChange={e => setEqForm({...eqForm, brand: e.target.value})} placeholder="Bambu Lab" data-testid="equipment-brand-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prezzo (€)</Label>
                    <Input type="number" step="0.01" value={eqForm.price} onChange={e => setEqForm({...eqForm, price: parseFloat(e.target.value) || 0})} className="font-mono" data-testid="equipment-price-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Vita Stimata (ore)</Label>
                    <Input type="number" value={eqForm.life_hours} onChange={e => setEqForm({...eqForm, life_hours: parseFloat(e.target.value) || 0})} className="font-mono" data-testid="equipment-life-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Input value={eqForm.notes} onChange={e => setEqForm({...eqForm, notes: e.target.value})} placeholder="Opzionale" />
                </div>
                <Card className="bg-muted/50 border-dashed">
                  <CardContent className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ammortamento/ora:</span>
                      <span className="font-mono text-primary">€{eqPreviewAmort.toFixed(4)}</span>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEqDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="save-equipment-btn">{editingEq ? 'Aggiorna' : 'Aggiungi'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {equipment.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold mb-2">Nessuna attrezzatura</h3>
              <p className="text-muted-foreground mb-4 text-sm">Aggiungi accessori come AMS, piatti, ecc.</p>
              <Button onClick={openNewEqDialog}><Plus className="w-4 h-4 mr-2" />Aggiungi</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {equipment.map(eq => (
                <Card key={eq.id} className="bg-muted/30 group" data-testid={`equipment-card-${eq.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold">{eq.name}</h3>
                          <p className="text-xs text-muted-foreground">{eq.brand}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleEqEdit(eq)} data-testid={`edit-equipment-${eq.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleEqDelete(eq.id)} data-testid={`delete-equipment-${eq.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground block">Prezzo</span>
                        <span className="font-mono">€{eq.price?.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground block">Vita</span>
                        <span className="font-mono">{eq.life_hours}h</span>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground block">€/h</span>
                        <span className="font-mono text-primary">€{eq.amortization_per_hour?.toFixed(4)}</span>
                      </div>
                    </div>
                    {eq.notes && <p className="text-xs text-muted-foreground mt-2">{eq.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-border/40 bg-primary/5">
        <CardContent className="p-4">
          <h4 className="font-heading font-semibold mb-2">Come funziona il calcolo</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><strong>Ammortamento:</strong> Costo stampante ÷ Ore di vita stimate</li>
            <li><strong>Elettricità:</strong> (Potenza W ÷ 1000) × Costo kWh</li>
            <li><strong>Costo Fisso/ora:</strong> Ammortamento + Elettricità</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
